<template>
  <Screen
    center-transparent
    :user="user"
    :group-id="selectedGroupId"
  >
    <section
      v-if="selectedGroup"
      slot="header-center"
    >
      <ElDropdown v-if="hasMultipleGroups">
        <ElButton
          type="text"
          class="el-dropdown-link"
        >
          {{
            selectedGroup.display_name || formatGroupName(selectedGroup.name)
          }}
          <i class="el-icon-arrow-down el-icon--right" />
        </ElButton>
        <ElDropdownMenu slot="dropdown">
          <span
            v-for="(group, index) in groups"
            :key="index"
            @click="handleSelectedGroupChange(group)"
          >
            <ElDropdownItem>{{
              group.display_name || formatGroupName(group.name)
            }}</ElDropdownItem>
          </span>
        </ElDropdownMenu>
      </ElDropdown>
      <span v-else>
        {{ selectedGroup.display_name || formatGroupName(selectedGroup.name) }}
      </span>
    </section>

    <section
      v-else
      slot="header-center"
    >
      <span>Digital Foamboard</span>
    </section>

    <div
      slot="screen-center"
      v-loading="isLoading"
      element-loading-text="Loading projects..."
      class="projects"
    >
      <div v-if="selectedGroup && selectedSeason">
        <header class="projects__header">
          <section class="projects__seasons">
            <div class="projects__seasons-heading">
              <ElDropdown v-if="hasMultipleSeasons">
                <ElButton class="el-dropdown-link">
                  {{ selectedSeason.name }}
                  <i class="el-icon-arrow-down el-icon--right" />
                </ElButton>
                <ElDropdownMenu slot="dropdown">
                  <span
                    v-for="(season, index) in seasons"
                    :key="index"
                    @click="handleSelectedSeasonChange(season)"
                  >
                    <ElDropdownItem>{{ season.name }}</ElDropdownItem>
                  </span>
                </ElDropdownMenu>
              </ElDropdown>
              <h3 v-else>
                {{ selectedSeason.name }}
              </h3>
            </div>
          </section>
          <div class="stitch-list-spaced-horizontal">
            <router-link
              :to="
                `/collection?group_id=${selectedGroup.id}&season_id=${selectedSeason.id}`
              "
            >
              <ElButton>
                Collection Overview
              </ElButton>
            </router-link>
            <ElButton
              v-if="isUserCreatorInGroup"
              type="primary"
              plain
              @click="toggleCreateNewProjectModal"
            >
              New Project
            </ElButton>
          </div>
        </header>
        <section>
          <div v-if="projectsVisible.length > 0">
            <div
              v-for="(project, index) in projectsVisible"
              :key="index"
              @click="showAllBoards(project.id)"
            >
              <ElCard
                class="projects__project"
                shadow="hover"
              >
                <div>
                  <h3 class="projects__project-title">
                    {{ project.name }}
                  </h3>
                  <p
                    v-if="project.boards"
                    class="projects__project-sub"
                  >
                    <small> {{ project.boards.length }} Boards </small>
                  </p>
                </div>

                <i class="projects__project-icon el-icon-arrow-right" />
              </ElCard>
              <br>
            </div>
          </div>
          <div v-else>
            No projects for this Division have been created in this Season yet.
          </div>
        </section>
      </div>

      <section
        v-else
        class="projects__project--no-groups"
      >
        <h2>Nothing to see yet 🙈</h2>
        <br>
        <h3>
          Ask your team lead or Divisional Merchandising team<br>to add you to
          a division.
        </h3>
      </section>

      <StitchDialog
        title="New project"
        :visible.sync="showCreateNewProjectModal"
      >
        <form @submit.prevent="createNewProject(projectName)">
          <div>
            <input
              ref="createNewProjectModalInput"
              v-model="projectName"
              type="text"
              placeholder="My project"
              size="60"
              autofocus
            >
          </div>
          <br>
          <div>
            <ElButton
              @click="showCreateNewProjectModal = false"
            >
              Cancel
            </ElButton>
            <ElButton
              type="primary"
              native-type="submit"
              :disabled="!projectName"
            >
              Looks good
            </ElButton>
          </div>
        </form>
      </StitchDialog>
    </div>
  </Screen>
</template>

<script>
import { mapActions } from 'vuex'
import Utils from '@/services/utils'
import { Workspace } from '@/mixins/workspace'
import { S3Trim } from '@/mixins/s3Trim'
import { imageResolutions } from '@/constants/image'
import Screen from '@/components/Screen.vue'

export default {
  name: 'Projects',

  components: {
    Screen
  },

  mixins: [Workspace, S3Trim],

  data: () => ({
    showCreateNewProjectModal: false,
    projectName: null,
    isLoading: true,
    formatGroupName: Utils.formatGroupName
  }),

  computed: {
    /**
     * @returns {Array}
     */
    projectsVisible () {
      return this.projects.filter(
        project => this.isUserCreatorInGroup || project.visible_to_sales
      )
    },

    /**
     * @returns {boolean}
     */
    hasMultipleGroups () {
      return this.groups.length > 1
    },

    /**
     * @returns {boolean}
     */
    hasMultipleSeasons () {
      return this.seasons.length > 1
    },

    /**
     * @returns {boolean}
     */
    isUserCreatorInGroup () {
      return Utils.isUserCreatorInGroup(this.user, this.selectedGroupId)
    }
  },

  async created () {
    this.checkRouteParameters()

    await this.initializeProjectList()
  },

  methods: {
    ...mapActions([
      'fetchProjects',
      'fetchSeasons',
      'addProject',
      'updateSelectedGroupId',
      'updateSelectedSeasonId'
    ]),

    /**
     */
    async fetchProjectsByGroupIdAndSeasonId () {
      if (this.selectedGroupId && this.selectedSeasonId) {
        await this.fetchProjects({
          groupId: this.selectedGroupId,
          seasonId: this.selectedSeasonId
        })
      }
    },

    /**
     * Get Seasons using groupId value
     */
    async fetchSeasonsByGroupId () {
      if (this.selectedGroupId) {
        await this.fetchSeasons({
          groupId: this.selectedGroupId
        })
      }
    },

    /**
     * @param {string} projectName
     */
    async createNewProject (projectName) {
      if (projectName) {
        const payload = {
          name: projectName,
          groupId: this.selectedGroupId,
          seasonId: this.selectedSeasonId
        }
        const project = await this.addProject(payload)
        this.$router.push({ path: `/projects/${project.id}/create` })
        this.showCreateNewProjectModal = false
      } else {
        this.$refs.createNewProjectModalInput.focus()
      }
    },

    /**
     * @param {number} projectId
     */
    showAllBoards (projectId) {
      this.$router.push({ path: `/projects/${projectId}/boards` })
    },

    /**
     */
    toggleCreateNewProjectModal () {
      this.showCreateNewProjectModal = !this.showCreateNewProjectModal

      this.$nextTick().then(() => {
        this.$refs.createNewProjectModalInput.focus()
      })
    },

    /**
     * @param   {object}        board
     * @param   {string}        resolution
     *
     * @returns {string | null}
     */
    getPreviewOptionImageUrl (board, resolution = imageResolutions.OPTION_FULL) {
      if (board.preview_option && board.preview_option.assets_file) {
        return this.s3Trim(board.preview_option.assets_file, resolution)
      } else {
        return null
      }
    },

    /**
     */
    async initializeProjectList () {
      this.isLoading = true
      await this.fetchSeasonsByGroupId()
      await this.fetchProjectsByGroupIdAndSeasonId()
      this.isLoading = false
    },

    /**
     * @param {object} group
     */
    async handleSelectedGroupChange (group) {
      if (group.id !== this.selectedGroupId) {
        const payload = {
          groupId: group.id
        }

        this.updateSelectedGroupId(payload)
        await this.fetchSeasonsByGroupId()
        await this.updateProjects()
      }
    },

    /**
     * @param {object} season
     */
    async handleSelectedSeasonChange (season) {
      if (season.id !== this.selectedSeasonId) {
        const payload = {
          seasonId: season.id
        }
        this.updateSelectedSeasonId(payload)
        await this.updateProjects()
      }
    },

    /**
     */
    async updateProjects () {
      this.updateRouteParameters()

      this.isLoading = true
      await this.fetchProjectsByGroupIdAndSeasonId()
      this.isLoading = false
    },

    /**
     */
    checkRouteParameters () {
      const {
        group_id: groupId,
        season_id: seasonId
      } = this.$route.query

      if (groupId) {
        this.updateSelectedGroupId({
          groupId: Number.parseInt(groupId)
        })
      }

      if (seasonId) {
        this.updateSelectedSeasonId({
          seasonId: Number.parseInt(seasonId)
        })
      }
    },

    /**
     */
    updateRouteParameters () {
      const newQuery = {
        group_id: this.selectedGroupId.toString(),
        season_id: this.selectedSeasonId.toString()
      }

      if (JSON.stringify(newQuery) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({
          query: newQuery
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$height-project-button: spacing(8);
$margin-dropdown-arrow: 6px;
$dropdown-menu-hax-height: 90vh;

$top-project-icon: $position-center;
$right-project-icon: 20px;

.projects {
  @include size(100%);
  @include scroll-y-smooth();

  position: absolute;
  top: 0;
  left: 0;
}

.projects__header {
  position: relative;
  display: flex;
  justify-content: space-between;
  margin: $spacer-unit-x2 0;
}

.projects__action {
  margin-left: $spacer-unit;
}

.projects__seasons {
  display: flex;
}

.projects__seasons-heading {
  position: relative;
  display: flex;
  align-items: center;
  font-size: $font-size-extra-large;
}

.projects__project {
  position: relative;
  cursor: pointer;

  &-icon {
    position: absolute;
    top: $top-project-icon;
    right: $right-project-icon;
    transform: translateY(-50%);
  }

  &-sub {
    margin: 0;
    font-size: $font-size-medium;
  }

  &-next-button {
    @include absolute-center-right;

    margin-right: $spacer-unit-x2;
  }

  &--no-groups {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;

    .email-link--3d-support {
      color: $blue;
      text-decoration: underline;
    }
  }
}

.projects__modal-header {
  color: $grey-light;
  font-size: $font-size-medium;
}

.projects__group {
  position: relative;
  display: flex;
  align-items: center;

  .projects__groups-dropdown {
    position: absolute;
    left: 100%;
    margin-left: $margin-dropdown-arrow;
  }
}

.el-dropdown-menu.el-popper {
  max-height: $dropdown-menu-hax-height;
  overflow-y: auto;
}
</style>
